import {put, call} from 'redux-saga/effects';
import MatterStage from "../../entities/matterStage";
import * as Actions from "../actions/matterStageActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type MatterStageAction = {
    matterStageId: string;
    matterStage: MatterStage;
    matterStages: MatterStage[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneMatterStage(action: MatterStageAction) {
	const session = tabSession.captureGeneration();
	try {
		const matterStage: MatterStage = yield call(api.findOne, 'matterStage', action.matterStageId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.MATTERSTAGE_FETCH_ONE_SUCCESS, matterStageId: action.matterStageId, matterStage});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.MATTERSTAGE_FETCH_ONE_FAILURE, matterStageId: action.matterStageId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {MatterStageAction} action
 */
export function* saveMatterStage(action: MatterStageAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ MATTERSTAGE_SAVE saga', action.matterStage?.id);
    
	if (action.matterStage === undefined || action.matterStage === null) {
		console.error('saveMatterStage received a null');
		throw new Error('saveMatterStage received a null');
	}

	if (!(action.matterStage instanceof MatterStage)) {
		action.matterStage = new MatterStage(action.matterStage);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'matterStage', action.matterStage);
		if (addResult.isErr()) {
			console.error(`⮂ saveMatterStage: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.MATTERSTAGE_SAVE_FAILURE, matterStage: action.matterStage, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.MATTERSTAGE_SAVE, matterStage: action.matterStage});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.MATTERSTAGE_SAVE_FAILURE, matterStage: action.matterStage, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {MatterStageAction} action
 * @param {MatterStage[]} action.matterStages
 */
export function* saveManyMatterStages(action: MatterStageAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ MATTERSTAGE_SAVE_MANY saga', action.matterStages.map(matterStage => matterStage.id).join(','));
    
    if (!Array.isArray(action.matterStages)) {
        console.error('saveManyMatterStages expected an array, received', action.matterStages);
        throw new Error('saveManyMatterStages expected an array');
    }

	try {
		const toSave: MatterStage[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.matterStages.length; i++) {
			const matterStage = action.matterStages[i];
			matterStage.persisted = false;
			toSave.push(matterStage);
		}

		const addResult = yield call(idb.add, 'matterStage', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyMatterStages: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.MATTERSTAGE_SAVE_MANY_FAILURE, matterStages: action.matterStages, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.MATTERSTAGE_SAVE_MANY, matterStages: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.MATTERSTAGE_SAVE_MANY_FAILURE, matterStages: action.matterStages, status: 500, err});

		console.error(`Error saving ${action.matterStages.length} matterStages to database`, err);
	}
}

/**
 * @param {MatterStageAction} action
 */
export function* removeMatterStage(action: MatterStageAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.matterStage === undefined || action.matterStage === null || !(action.matterStage instanceof MatterStage)) {
			console.error('removeMatterStage expected type MatterStage, received', action.matterStage);
			throw new Error('removeMatterStage expected type MatterStage');
		}

	try {
		// Mark as deleted
		action.matterStage.status = 'D';
		action.matterStage.addedOn = newHRTimestamp();
		action.matterStage.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'matterStage', action.matterStage);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeMatterStage: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.MATTERSTAGE_REMOVE_FAILURE, matterStage: action.matterStage, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.MATTERSTAGE_REMOVE, matterStage: action.matterStage});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.MATTERSTAGE_REMOVE_FAILURE, matterStage: action.matterStage, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this MatterStage.
	*
	* @param {MatterStageAction} action
	*/
export function* restoreMatterStage(action: MatterStageAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreMatterStage expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.matterStage === undefined || action.matterStage === null) {
        console.error('restoreMatterStage expected a MatterStage, received null');
        throw new Error('restoreMatterStage expected a MatterStage, received null');
    }
    else if (!(action.matterStage instanceof MatterStage)) action.matterStage = new MatterStage(action.matterStage); 
    
	try {
		// Mark as restored
		action.matterStage.status = 'U';
		action.matterStage.addedOn = newHRTimestamp();
		action.matterStage.addedBy = userId;
		action.matterStage.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'matterStage', action.matterStage);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreMatterStage: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.MATTERSTAGE_RESTORE_FAILURE, matterStage: action.matterStage, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.MATTERSTAGE_RESTORE, matterStage: action.matterStage});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.MATTERSTAGE_RESTORE_FAILURE, matterStage: action.matterStage, status: 500, err});

		console.error('Error restoring from database', err);
	}
}